import React, {useState, useContext, useRef, useEffect, useLayoutEffect} from 'react';

import {
  Button,
  ButtonWithPopOut,
  constants,
  useWindowSize,
} from '@newsela/angelou';
import {useFeature} from '@optimizely/react-sdk';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import {css} from 'emotion';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';

import withTrackEvent from 'static/three-oh/src/components/with/WithTrackEvent';
import fetcher3 from 'static/three-oh/src/modules/apis/ApiFetcher3';
import {searchRoot} from 'static/three-oh/src/modules/constants/routePaths';
import {getCollections} from 'static/three-oh/src/modulesV2/selectors';
import {
  AGATHA_PAGE_SEARCH_FORM_ID,
  GLOBAL_NAV_SEARCH_FORM_ID
} from 'static/three-oh/src/routes/Search/constants';
import {updateReferrer} from 'static/three-oh/utils/sessionActivity';
import {ReactComponent as AssignmentsIcon} from 'staticImages/icons/globalNavIcons/assignments.svg';
import {ReactComponent as BinderIcon} from 'staticImages/icons/globalNavIcons/binder.svg';
import {ReactComponent as YourContentIcon} from 'staticImages/icons/globalNavIcons/bookmark-o.svg';
import {ReactComponent as BrowseIcon} from 'staticImages/icons/globalNavIcons/browse.svg';
import {ReactComponent as SearchIcon} from 'staticImages/icons/globalNavIcons/search.svg';

import useFocusOut from '../../../../utils/useFocusOut';
import {
  INACTIVE_INDEX_VALUE,
  ASSIGNMENTS_LABEL,
  BINDER_LABEL,
  BROWSE_LABEL,
  SEARCH_LABEL,
  YOUR_CONTENT_LABEL
} from '../constants';
import {GlobalLayoutRefsContext} from '../GlobalLayoutRefsContext';
import BinderDrawer from './DrawerContents/BinderDrawer';
import BrowseDrawer from './DrawerContents/BrowseDrawer';
import {
  ELA_CONSTANTS,
  SCIENCE_CONSTANTS,
  SOCIAL_STUDIES_CONSTANTS,
} from './DrawerContents/constants';
import YourContentDrawer from './DrawerContents/YourContentDrawer';
import GlobalNavMenuItem from './GlobalNavMenuItem';
import GlobalNavSearch from './GlobalNavSearch/GlobalNavSearch';
import {
  assignmentIconStyle,
  buttonStyles,
  globalNavStyle,
  iconStyle,
  buttonWithPopOutStyle,
  buttonWithPopOutStyleNavRefresh,
  liStyle,
  topLevelLinkStyle,
  ulStyle,
  ulStyleNavRefresh,
  ulStyleGlobalSearch,
  inactiveButtonStyle,
  searchFormStyle,
} from './style';

const propTypes = {
  hideGlobalNav: PropTypes.bool,
  history: PropTypes.object,
  user: PropTypes.object.isRequired,
  userProperties: PropTypes.object.isRequired,
};

const BrowseSubjectPages = [
  ELA_CONSTANTS,
  SCIENCE_CONSTANTS,
  SOCIAL_STUDIES_CONSTANTS,
];

const ButtonWithTrackEvent = withTrackEvent(Button);
const ButtonWithPopOutWithTrackEvent = withTrackEvent(ButtonWithPopOut);
const {
  legacy_breakpoints: {
    Breakpoints: {sm, xs},
  },
} = constants;

export const AnchorWithTrackEvent = withTrackEvent((props) => (
  <a {...props}>{props.children}</a>
));

export const GlobalNav = React.forwardRef((props, ref) => {
  GlobalNav.displayName = 'GlobalNav';

  const {hideGlobalNav, history, user, userProperties} = props;

  // The Global Nav search is behind a feature flag
  const [isGlobalNavRefreshEnabled] = useFeature('global_nav_refresh', {autoUpdate: true});

  /**
   * Each button in the nav has an index number.
   * The onClick handler sets the openIndex to the button's index.
   * If openIndex === INACTIVE_INDEX_VALUE, all nav drawers are closed.
   */
  const [openIndex, setOpenIndex] = useState(INACTIVE_INDEX_VALUE);
  const {globalNavRef} = useContext(GlobalLayoutRefsContext);
  const assignmentsButtonRef = useRef(null);
  const searchButtonRef = useRef(null);
  const browseDrawerRef = useRef(null);
  const browseButtonRef = useRef(null);
  const yourContentDrawerRef = useRef(null);
  const yourContentButtonRef = useRef(null);
  const binderDrawerRef = useRef(null);
  const binderButtonRef = useRef(null);
  const {width: windowWidth} = useWindowSize();
  const isDesktopDisplay = windowWidth > sm;
  const visuallyHideLabel = windowWidth < xs;

  // If this boolean is true, based on a student's district, the Search and Browse options will not be rendered in the Global Nav
  const [userIsInAllCATDistrict, setUserIsInAllCATDistrict] = useState(true);

  useLayoutEffect(() => {
    if (windowWidth <= sm) {
      if (openIndex !== INACTIVE_INDEX_VALUE) {
        disableBodyScroll(ref.current);
      } else {
        enableBodyScroll(ref.current);
      }
    }
  }, [windowWidth, openIndex]);

  useEffect(() => {
    const getUserAttributes = async() => {
      const userAttributes = await fetcher3.get('user/attributes/');
      setUserIsInAllCATDistrict(userAttributes?.data?.in_district_with_all_advisory_content);
    };
    getUserAttributes();
  }, []);

  // Assign an index to each button in the global-navigation menu.
  const buttonIndices = {
    searchButtonIndex: 0,
    browseButtonIndex: 1,
    yourContentButtonIndex: 2,
    assignmentsButtonIndex: 3,
    binderButtonIndex: 4,
  };

  const focusRef = useFocusOut(() => {
    setOpenIndex(INACTIVE_INDEX_VALUE);
  }, openIndex >= 0);

  const handleOnEscape = (ref, buttonRef, setOpenIndex) => {
    // The search logic is distinct: if search is activated (the search form
    // is on display) hitting 'Escape' will return focus to the global-nav's
    // 'Search' button. The search content-drawer is anchored to the search
    // form (not the Search button...like the other content-drawers are) so
    // this distinction is necessary.
    if (openIndex === buttonIndices.searchButtonIndex) {
      buttonRef.current.focus();
    } else if (
      ref &&
      ref.current &&
      ref.current.contains(document.activeElement)
    ) {
      buttonRef.current.focus();
    }
    setOpenIndex();
  };

  const handleOnClickOff = (event, buttonRef) => {
    // Clicking into the search form's input-field  or submit button should not
    // trigger a click-off event.
    const searchIsActive = openIndex === buttonIndices.searchButtonIndex;
    const isSearchFormInputField = event.target.id === GLOBAL_NAV_SEARCH_FORM_ID;
    const searchFormSubmitButton = document.querySelector('.globalNavSearchSubmitButton');
    const isSearchFormSubmitButton = event.target === searchFormSubmitButton;
    const notAClickOffEvent = (
      searchIsActive && (isSearchFormInputField || isSearchFormSubmitButton)
    );

    if (!notAClickOffEvent && !buttonRef.current.contains(event.target)) {
      setOpenIndex(INACTIVE_INDEX_VALUE);
      buttonRef.current.focus();
    }
  };

  const handleOnCancel = (buttonRef) => {
    setOpenIndex(INACTIVE_INDEX_VALUE);
    buttonRef.current.focus();
  };

  const handleClick = (index) => {
    if (openIndex !== index) {
      setOpenIndex(index);
    } else {
      setOpenIndex(INACTIVE_INDEX_VALUE);
    }
  };

  const handleSearchClick = (index) => {
    // There's an issue with doing client side routing to search from Angular pages --
    // it appears like Angular and React both fight for control of routing behaviour,
    // and it totally screws with the user experience. If we're on a page that's based
    // off Angular, let's just server-side redirect to the search page.
    if (window && window.angular !== undefined) {
      window.location.href = searchRoot;
    } else {
      handleClick(index);
    }
  };

  const handleReferrer = (e) => {
    if (e && e.target) {
      const tagName = e.target.tagName?.toLowerCase();
      let source;
      if (tagName === 'a' && browseDrawerRef?.current?.contains(e.target)) {
        source = BROWSE_LABEL;
      } else if (tagName === 'a' && yourContentDrawerRef?.current?.contains(e.target)) {
        source = YOUR_CONTENT_LABEL;
      } else if (tagName === 'a' && binderDrawerRef?.current?.contains(e.target)) {
        source = BINDER_LABEL;
      } else if (assignmentsButtonRef?.current?.contains(e.target)) {
        source = ASSIGNMENTS_LABEL;
      } else if (searchButtonRef?.current?.contains(e.target)) {
        source = SEARCH_LABEL;
      }
      if (source) {
        updateReferrer(source);
      }
    }
  };

  const allCollections = useSelector((state) => getCollections(state));
  const {
    isAuthorizedDistrictLeader,
    isAuthorizedPrincipal,
    isDistrictLeader,
    isFreeUser,
    isParent,
    isPrincipal,
    isStudent,
    isTeacher,
    isAdminReporting2022User,
    hasActiveProductLicense
  } = userProperties;

  const buttonProps = {
    __classNameFor: {root: css(buttonStyles)},
    legacy_flavor: Button.legacy_flavor.incognito,
  };

  history.listen(() => {
    setOpenIndex(INACTIVE_INDEX_VALUE);
  });

  const searchIsActive = openIndex === buttonIndices.searchButtonIndex;

  const inactiveLineItemStyle = (
    (
      searchIsActive &&
      isDesktopDisplay
    ) ?
      inactiveButtonStyle :
      null
  );

  const searchButton = (
    <li
      className={css(liStyle, inactiveLineItemStyle)}
      data-qa-selector="search_button_masthead"
    >
      <ButtonWithTrackEvent
        objectType="Content"
        actionPrefix={searchIsActive ? 'Cancel' : 'Initiate'}
        actionName="Search"
        legacyEventName="GlobalNav menu item-Toggle search"
        streamProperties={{description: `${searchIsActive ? 'Close' : 'Open'} Search input navigation dropdown menu.`}}
        ariaProps={{
          'aria-expanded': searchIsActive,
          'aria-haspopup': true,
        }}
        onClick={(e) => {
          const searchPageInputField = document.getElementById(AGATHA_PAGE_SEARCH_FORM_ID);

          if (
            window.location.pathname === searchRoot &&
            searchPageInputField
          ) {
            searchPageInputField.focus();
          } else {
            if (isGlobalNavRefreshEnabled) {
              handleSearchClick(buttonIndices.searchButtonIndex);
            } else {
              handleReferrer(e);
              window.location.href = searchRoot;
            }
          }
        }}
        ref={searchButtonRef}
        {...buttonProps}
      >
        <GlobalNavMenuItem
          displayChevron={false}
          iconElement={
            <SearchIcon
              aria-hidden="true"
              className={css(iconStyle(openIndex === buttonIndices.searchButtonIndex))}
              itemLabel="Search"
            />
          }
          isActive={openIndex === buttonIndices.searchButtonIndex}
          itemLabel="Search"
          isNewGlobalNav={isGlobalNavRefreshEnabled}
        />
      </ButtonWithTrackEvent>
      <GlobalNavSearch
        show={(openIndex === buttonIndices.searchButtonIndex && !isDesktopDisplay)}
        history={props.history}
        userProperties={userProperties}
        user={user}
        handleOnCancel={handleOnCancel}
        handleOnClickOff={handleOnClickOff}
        handleOnEscape={handleOnEscape}
        isDesktopDisplay={isDesktopDisplay}
        ref={{
          searchButtonRef: searchButtonRef,
          browseButtonRef: browseButtonRef,
        }}
        setOpenIndex={setOpenIndex}
      />
    </li>
  );

  const isContentVisible = (isSectionActive) =>
    Boolean(isSectionActive && globalNavRef.current);

  // Other roles won't return binder menu options
  const shouldHaveBinderOptions = (
    isStudent ||
    isTeacher ||
    (!isFreeUser && isAuthorizedDistrictLeader && hasActiveProductLicense) ||
    (!isFreeUser && isAuthorizedPrincipal && hasActiveProductLicense)
  );

  const browseIsActive = openIndex === buttonIndices.browseButtonIndex;

  const browseButton = (
    <li
      className={css(liStyle, inactiveLineItemStyle)}
      data-qa-selector="browse_button_masthead"
      ref={browseDrawerRef}
    >
      <ButtonWithPopOutWithTrackEvent
        id="globalNavBrowse"
        objectType="Content"
        actionPrefix={browseIsActive ? 'Cancel' : 'Initiate'}
        actionName="Navigate"
        dataProps={{'data-qa-selector': 'your_content_nav_link'}}
        legacyEventName="GlobalNav menu item-Toggle browse"
        streamProperties={{
          description: `${
            browseIsActive ? 'Close' : 'Open'
          } Browse dropdown navigation dropdown menu.`,
        }}
        ariaProps={{
          'aria-expanded': browseIsActive,
          'aria-haspopup': true,
        }}
        onClickButton={() => {
          handleClick(buttonIndices.browseButtonIndex);
        }}
        onClickOff={(e) => handleOnClickOff(e, browseButtonRef)}
        onEsc={() => {
          handleOnEscape(browseDrawerRef, browseButtonRef, () =>
            setOpenIndex(INACTIVE_INDEX_VALUE)
          );
        }}
        ref={browseButtonRef}
        isPopOutVisible={isContentVisible(browseIsActive)}
        __cssFor={isGlobalNavRefreshEnabled && isDesktopDisplay ? buttonWithPopOutStyleNavRefresh : buttonWithPopOutStyle}
        enableDrawerMode
        drawerButtonIcon={
          <GlobalNavMenuItem
            displayChevron={false}
            iconElement={
              <BrowseIcon
                aria-hidden="true"
                className={css(iconStyle(browseIsActive))}
                itemLabel={BROWSE_LABEL}
              />
            }
          />
        }
        drawerButtonTitle={visuallyHideLabel ? '' : BROWSE_LABEL}
        buttonContents={BROWSE_LABEL}
        popOutContents={
          <BrowseDrawer collections={allCollections} history={history} isNewGlobalNav={isDesktopDisplay && isGlobalNavRefreshEnabled}/>
        }
      />
    </li>
  );
  const yourContentIsActive =
    openIndex === buttonIndices.yourContentButtonIndex;
  const yourContentButton = (
    <li
      className={css(liStyle, inactiveLineItemStyle)}
      data-qa-selector="your_content_button_masthead"
      ref={yourContentDrawerRef}
    >
      <ButtonWithPopOutWithTrackEvent
        id="globalNavYourContent"
        onClickButton={() => {
          handleClick(buttonIndices.yourContentButtonIndex);
        }}
        objectType="Content"
        actionPrefix={yourContentIsActive ? 'Cancel' : 'Initiate'}
        actionName="Navigate"
        legacyEventName="GlobalNav menu item-Toggle your content"
        streamProperties={{
          description: `${
            yourContentIsActive ? 'Close' : 'Open'
          } Your Content navigation dropdown menu.`,
        }}
        onClickOff={(e) => handleOnClickOff(e, yourContentButtonRef)}
        onEsc={() => {
          handleOnEscape(yourContentDrawerRef, yourContentButtonRef, () =>
            setOpenIndex(INACTIVE_INDEX_VALUE)
          );
        }}
        ref={yourContentButtonRef}
        isPopOutVisible={isContentVisible(yourContentIsActive)}
        enableDrawerMode
        drawerButtonIcon={
          <GlobalNavMenuItem
            displayChevron={false}
            iconElement={
              <YourContentIcon
                aria-hidden="true"
                className={css(iconStyle(yourContentIsActive))}
                itemLabel={YOUR_CONTENT_LABEL}
              />
            }
          />
        }
        drawerButtonTitle={visuallyHideLabel ? '' : YOUR_CONTENT_LABEL}
        buttonContents={YOUR_CONTENT_LABEL}
        __cssFor={buttonWithPopOutStyle}
        popOutContents={
          <YourContentDrawer
            history={history}
            userProperties={userProperties}
          />
        }
      />
    </li>
  );

  const shouldShowAssignmentsButton = isTeacher || isStudent || isParent;
  const {isAuthenticated} = userProperties;

  const assignmentsButton = shouldShowAssignmentsButton ? (
    <li className={css(liStyle, inactiveLineItemStyle)} data-qa-selector="assignments_button_masthead" ref={assignmentsButtonRef}>
      <AnchorWithTrackEvent
        eventProperties={{
          description: 'global nav assignments link',
        }}
        objectType="Assignment"
        actionName="Navigate"
        legacyEventName="GlobalNav menu item-Toggle assignments"
        streamProperties={{
          description: 'Global navigation Assignments link.',
        }}
        className={css(topLevelLinkStyle)}
        href={isStudent ? '/assignments' : '/binder/assignments'}
        data-qa-selector="binder_assignments_link"
      >
        <GlobalNavMenuItem
          iconElement={
            <AssignmentsIcon
              aria-hidden="true"
              className={css(assignmentIconStyle)}
            />
          }
          itemLabel="Assignments"
          displayChevron={false}
          isNewGlobalNav={isGlobalNavRefreshEnabled}
        />
      </AnchorWithTrackEvent>
    </li>
  ) : null;

  const useReportingLabel = isAdminReporting2022User && (isPrincipal || isDistrictLeader);
  const binderIsActive = openIndex === buttonIndices.binderButtonIndex;
  const binderButton = (
    <li className={css(liStyle, inactiveLineItemStyle)} ref={binderDrawerRef}>
      {shouldHaveBinderOptions ? (
        <ButtonWithPopOutWithTrackEvent
          id={useReportingLabel ? 'globalNavReporting' : 'globalNavBinder'}
          onClickButton={() => {
            handleClick(buttonIndices.binderButtonIndex);
          }}
          actionPrefix={binderIsActive ? 'Cancel' : 'Initiate'}
          actionName="Navigate"
          legacyEventName={useReportingLabel ? 'GlobalNav menu item-Toggle reporting' : 'GlobalNav menu item-Toggle binder'}
          streamProperties={{
            description: `${binderIsActive ? 'Close' : 'Open'
              } ${useReportingLabel ? 'Reporting' : BINDER_LABEL} navigation dropdown menu.`,
          }}
          onClickOff={(e) => handleOnClickOff(e, binderButtonRef)}
          onEsc={() => {
            handleOnEscape(binderDrawerRef, binderButtonRef, () =>
              setOpenIndex(INACTIVE_INDEX_VALUE)
            );
          }}
          ref={binderButtonRef}
          isPopOutVisible={isContentVisible(binderIsActive)}
          __cssFor={buttonWithPopOutStyle}
          enableDrawerMode
          drawerButtonIcon={
            <GlobalNavMenuItem
              displayChevron={false}
              iconElement={
                <BinderIcon
                  aria-hidden="true"
                  className={css(iconStyle(binderIsActive))}
                  itemLabel={useReportingLabel ? 'Reporting' : BINDER_LABEL}
                />
              }
            />
          }
          drawerButtonTitle={visuallyHideLabel ? '' : useReportingLabel ? 'Reporting' : BINDER_LABEL}
          buttonContents={useReportingLabel ? 'Reporting' : BINDER_LABEL}
          popOutContents={
            <BinderDrawer history={history} userProperties={userProperties} />
          }
        />
      ) : (
        <AnchorWithTrackEvent
          id="globalNavBinder"
          eventProperties={{
            description: 'global nav assignments link',
          }}
          objectType="Assignment"
          actionName="Navigate"
          legacyEventName="GlobalNav menu item-Toggle binder"
          streamProperties={{
            description: `${binderIsActive ? 'Close' : 'Open'} Binder navigation dropdown menu.`,
          }}
          className={css(topLevelLinkStyle)}
          href={isStudent ? '/assignments' : '/binder/'}
        >
          <GlobalNavMenuItem
            displayChevron={false}
            iconElement={
              <BinderIcon
                aria-hidden="true"
                className={css(iconStyle(binderIsActive))}
              />
}
            itemLabel={BINDER_LABEL}
          />
        </AnchorWithTrackEvent>
      )}
    </li>
  );

  const globalNavButtons = (
    <>
      {userIsInAllCATDistrict ? null : searchButton}
      {isDesktopDisplay && isGlobalNavRefreshEnabled && (
        BrowseSubjectPages.map(({slug, name}) => {
          return (
            <li className={css(liStyle, inactiveLineItemStyle)} key={slug}>
              <AnchorWithTrackEvent
                eventProperties={{
                  description: `global nav ${name} link`,
                }}
                objectType={name}
                actionName="Navigate"
                legacyEventName={`GlobalNav menu item ${name}`}
                streamProperties={{
                  description: `Global navigation ${name} link.`,
                }}
                className={css(topLevelLinkStyle)}
                href={slug}
                data-qa-selector={`binder_${slug.replace('-', '_')}_link`}
              >
                <GlobalNavMenuItem
                  iconElement={
                    <AssignmentsIcon
                      aria-hidden="true"
                      className={css(assignmentIconStyle)}
                    />
                        }
                  itemLabel={name}
                  displayChevron={false}
                />
              </AnchorWithTrackEvent>
            </li>
          );
        })
      )}
      {userIsInAllCATDistrict ? null : browseButton}
      {isAuthenticated ? yourContentButton : null}
      {isAuthenticated ? assignmentsButton : null}
      {isAuthenticated ? binderButton : null}
    </>
  );

  const searchInput = (
    <div className={css(liStyle, searchFormStyle)}>
      <GlobalNavSearch
        show={openIndex === buttonIndices.searchButtonIndex}
        history={props.history}
        userProperties={userProperties}
        user={user}
        handleOnClickOff={(event) => handleOnClickOff(event, searchButtonRef)}
        handleOnEscape={handleOnEscape}
        handle
        isDesktopDisplay={isDesktopDisplay}
        ref={{
          searchButtonRef: searchButtonRef,
          browseButtonRef: browseButtonRef,
        }}
        setOpenIndex={setOpenIndex}
        handleOnCancel={handleOnCancel}
      />
    </div>
  );

  const isSearchActivated = isDesktopDisplay && openIndex === buttonIndices.searchButtonIndex;
  const globalNavDesktopDisplay = isSearchActivated ? searchInput : globalNavButtons;

  const WrapperElement = isSearchActivated ? 'div' : 'ul';

  const wrapperElementStyles = [
    ulStyle,
    isGlobalNavRefreshEnabled && openIndex === buttonIndices.searchButtonIndex && isDesktopDisplay && ulStyleGlobalSearch,
    isGlobalNavRefreshEnabled && ulStyleNavRefresh
  ];

  return (
    <nav ref={ref} className={css(globalNavStyle(hideGlobalNav))}>
      <WrapperElement
        className={css(...wrapperElementStyles)}
        data-qa-selector="global_nav_bar"
        onClick={handleReferrer}
        ref={focusRef}
      >
        {isDesktopDisplay ? globalNavDesktopDisplay : globalNavButtons}
      </WrapperElement>
    </nav>
  );
});

GlobalNav.propTypes = propTypes;
export default GlobalNav;
